import type { LexicalEditor, NodeKey } from 'lexical'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection'
import { mergeRegister } from '@lexical/utils'
import {
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical'
import React, { FC, useState } from 'react'
import { useCallback, useEffect, useRef } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Box, Button } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'

import { $isImageNode, ImagePayload } from '../ImageNode/ImageNode'
import { ImageDialog } from '../../ui/ImageDialog'
import { useCustomTranslation } from '../../../../Hooks/useCustomTranslation'
import { SMALL_IMAGE_SIZE } from './const'
import { getImageNaturalSizes, GetImageNaturalSizesResult } from '../../utils'

const useStyles = makeStyles()(theme => ({
  imageContainer: {
    position: 'relative',
    display: 'inline-flex',

    ':hover': {
      button: {
        visibility: 'visible',
        opacity: 1,
      },
    },
  },
  button: {
    padding: theme.spacing(1),
    minWidth: 0,
    minHeight: 0,
    svg: {
      height: 'auto',
      width: 16,
    },
    borderRadius: theme.shape.borderRadius,
    visibility: 'hidden',
    opacity: 0,
    position: 'absolute',
    top: theme.spacing(2),
    left: theme.spacing(2),
    transition: '0.3s',
  },
  smallImageButton: {
    top: 'calc(50% - 11px)',
    left: 'calc(50% - 11px)',
    borderRadius: theme.spacing(1),
    svg: {
      width: 10,
    },
  },
  selectedImage: {
    border: `2px solid ${theme.palette.buttonGroup.borderColorActive}`,
  },
}))

interface ImageComponentProps {
  nodeKey: NodeKey
  imageData: ImagePayload
}

/**
 * Custom image component which using for display inserting images in the markdown editor
 */
export const ImageComponent: FC<ImageComponentProps> = ({ imageData, nodeKey }) => {
  const { altText, height, src, width } = imageData
  const { classes } = useStyles()
  const imageRef = useRef<null | HTMLImageElement>(null)
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey)
  const [editor] = useLexicalComposerContext()
  const activeEditorRef = useRef<LexicalEditor | null>(null)
  const [isOpenedEditDialog, setOpenedEditDialog] = useState<boolean>(false)
  const { t } = useCustomTranslation()
  const [isSmallPicture, setSmallPicture] = useState(false)

  useEffect(() => {
    const { width, height, src } = imageData

    const checkIfPictureSmall = async (): Promise<void> => {
      let naturalSizes: GetImageNaturalSizesResult | null = null

      try {
        naturalSizes = await getImageNaturalSizes(src)
      } catch (err) {
        console.error(err)
      }

      const initialWidth = naturalSizes?.width || 0
      const initialHeight = naturalSizes?.height || 0

      if (width) {
        setSmallPicture(+width <= SMALL_IMAGE_SIZE)
        return
      }

      if (height) {
        setSmallPicture(+height <= SMALL_IMAGE_SIZE)
        return
      }

      setSmallPicture(initialWidth <= SMALL_IMAGE_SIZE || initialHeight <= SMALL_IMAGE_SIZE)
    }

    checkIfPictureSmall()
  }, [imageData])

  const onDelete = useCallback(
    (payload: KeyboardEvent) => {
      if (isSelected && $isNodeSelection($getSelection())) {
        const event: KeyboardEvent = payload
        event.preventDefault()
        const node = $getNodeByKey(nodeKey)
        if ($isImageNode(node)) {
          node.remove()
        }
      }
      return false
    },
    [isSelected, nodeKey]
  )

  const onClick = useCallback(
    (payload: MouseEvent) => {
      const event = payload

      if (event.target === imageRef.current) {
        if (event.shiftKey) {
          setSelected(!isSelected)
        } else {
          clearSelection()
          setSelected(true)
        }
        return true
      }

      return false
    },
    [isSelected, setSelected, clearSelection]
  )

  const updateImageHandler = (newData: ImagePayload): void => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey)
      if ($isImageNode(node)) {
        node.updateFullData(newData)
      }
    })
  }

  const closeImageUpdatingDialog = (): void => {
    setOpenedEditDialog(false)
  }

  const openImageUpdatingDialog = (): void => {
    setOpenedEditDialog(true)
  }

  useEffect(() => {
    const unregister = mergeRegister(
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_, activeEditor) => {
          activeEditorRef.current = activeEditor
          return false
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand<MouseEvent>(CLICK_COMMAND, onClick, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_DELETE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_BACKSPACE_COMMAND, onDelete, COMMAND_PRIORITY_LOW)
    )

    return () => {
      unregister()
    }
  }, [clearSelection, editor, isSelected, nodeKey, onDelete, onClick, setSelected])

  return (
    <>
      <Box className={classes.imageContainer}>
        <Button
          onClick={openImageUpdatingDialog}
          aria-label={t('shell.markdown-editor.edit-image')}
          title={t('shell.markdown-editor.edit-image')}
          className={isSmallPicture ? `${classes.button} ${classes.smallImageButton}` : classes.button}
          variant="contained"
        >
          <EditIcon />
        </Button>
        <img
          className={isSelected ? classes.selectedImage : ''}
          src={src}
          alt={altText}
          ref={imageRef}
          width={width}
          height={height}
          draggable={false}
        />
      </Box>
      {isOpenedEditDialog && (
        <ImageDialog onClose={closeImageUpdatingDialog} onSave={updateImageHandler} open={isOpenedEditDialog} initialFormData={imageData} />
      )}
    </>
  )
}
